import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
    LinkIcon,
    PhotoIcon,
    MegaphoneIcon,
} from '@heroicons/react/24/outline';
import { DrawerCustom } from '../../../components/drawer/Drawer';
import { Button } from '../../../components/buttons/Button';
import { InputTextModern } from '../../../components/inputs/InputTextModern';
import { TextareaModern } from '../../../components/textareaModern';
import { CourseFormSection } from '../components/CourseFormSection';
import { TaskAttachmentField } from '../tasks/TaskAttachmentField';
import {
    createNewNoticeMessage,
    getNoticeMessages,
    updateNoticeMessage,
} from '../../../redux/actions/noticeMessage';
import { validUrl } from '../../../common/urlValid';
import { formatFileSize, getFileExtension } from '../../../common/taskUploads';
import '../tasks/task-form.css';

const SECTION_ICON_CLASS = 'h-5 w-5';
const MESSAGE_MAX = 2000;
const NOTICE_IMAGE_ACCEPT = '.png,.jpeg,.jpg,image/png,image/jpeg';
const NOTICE_IMAGE_HINT = 'PNG, JPEG o JPG. Máximo 3 MB.';
const NOTICE_IMAGE_MAX_BYTES = 3 * 1024 * 1024;
const NOTICE_IMAGE_EXTENSIONS = ['.png', '.jpeg', '.jpg'];

const emptyForm = {
    message: '',
    link: '',
};

const createEmptyErrors = () => ({
    message: '',
    link: '',
    file: '',
});

const guessMimeFromUrl = (url = '') => {
    const lower = String(url).toLowerCase();
    if (lower.includes('.png')) return 'image/png';
    return 'image/jpeg';
};

const validateNoticeImage = (file) => {
    if (!file) {
        return { ok: false, message: 'Selecciona una imagen.' };
    }

    const extension = getFileExtension(file.name);
    if (!NOTICE_IMAGE_EXTENSIONS.includes(extension)) {
        return {
            ok: false,
            message: 'Solo se permiten imágenes .png, .jpeg y .jpg.',
        };
    }

    if (file.size > NOTICE_IMAGE_MAX_BYTES) {
        return {
            ok: false,
            message: `La imagen no puede superar 3 MB (peso: ${formatFileSize(file.size)}).`,
        };
    }

    return { ok: true };
};

export const NoticeFormDrawer = ({
    isOpen,
    onClose,
    courseId,
    userId,
    notice = null,
}) => {
    const dispatch = useDispatch();
    const isEdit = Boolean(notice?._id);

    const [form, setForm] = useState(emptyForm);
    const [stagedImage, setStagedImage] = useState(null);
    const [existingImage, setExistingImage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formErrors, setFormErrors] = useState(createEmptyErrors);
    const [showValidationSummary, setShowValidationSummary] = useState(false);
    const [fieldsKey, setFieldsKey] = useState(0);
    const scrollRef = useRef(null);
    const draftEntityKeyRef = useRef(null);
    const previewUrlRef = useRef(null);

    const draftEntityKey = isEdit && notice?._id
        ? `edit:${notice._id}`
        : `create:${courseId || 'new'}`;

    const isProcessing = saving;

    const revokePreviewUrl = useCallback(() => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
    }, []);

    const resetValidationState = useCallback(() => {
        setFormErrors(createEmptyErrors());
        setShowValidationSummary(false);
    }, []);

    const clearFieldError = useCallback((field) => {
        setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }, []);

    const clearStagedImage = useCallback(() => {
        revokePreviewUrl();
        setStagedImage(null);
    }, [revokePreviewUrl]);

    const resetDraft = useCallback(() => {
        draftEntityKeyRef.current = null;
        setForm(emptyForm);
        clearStagedImage();
        setExistingImage(null);
        setSaving(false);
        resetValidationState();
    }, [clearStagedImage, resetValidationState]);

    const hydrateForm = useCallback(() => {
        clearStagedImage();
        if (notice) {
            setForm({
                message: notice.message || '',
                link: notice.link || '',
            });
            setExistingImage(notice.urlName
                ? {
                    fileName: 'Imagen del aviso',
                    url: notice.urlName,
                    mimeType: guessMimeFromUrl(notice.urlName),
                    size: null,
                }
                : null);
        } else {
            setForm(emptyForm);
            setExistingImage(null);
        }
        setSaving(false);
        resetValidationState();
    }, [notice, clearStagedImage, resetValidationState]);

    useEffect(() => {
        if (!isOpen) return;

        setFieldsKey((key) => key + 1);

        if (draftEntityKeyRef.current === draftEntityKey) {
            return;
        }

        draftEntityKeyRef.current = draftEntityKey;
        hydrateForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, draftEntityKey]);

    useEffect(() => () => {
        revokePreviewUrl();
    }, [revokePreviewUrl]);

    const handleDismiss = () => {
        if (isProcessing) return;
        onClose({ reset: false });
    };

    const handleCancel = () => {
        if (isProcessing) return;
        resetDraft();
        onClose({ reset: true });
    };

    const handleSelectImage = (file) => {
        const validation = validateNoticeImage(file);
        if (!validation.ok) {
            setFormErrors((prev) => ({ ...prev, file: validation.message }));
            toast.error(validation.message);
            return;
        }

        revokePreviewUrl();
        const previewUrl = URL.createObjectURL(file);
        previewUrlRef.current = previewUrl;

        setExistingImage(null);
        setStagedImage({
            id: 'notice-image',
            file,
            fileName: file.name,
            size: file.size,
            mimeType: file.type || (getFileExtension(file.name) === '.png' ? 'image/png' : 'image/jpeg'),
            previewUrl,
            status: 'ready',
            progress: null,
            error: null,
        });
        clearFieldError('file');
    };

    const handleRemoveExisting = () => {
        setExistingImage(null);
        clearFieldError('file');
    };

    const handleRemoveStaged = () => {
        clearStagedImage();
        clearFieldError('file');
    };

    const validateForm = () => {
        const errors = createEmptyErrors();
        const message = form.message.trim();
        const link = form.link.trim();

        if (!message) {
            errors.message = 'Escribe el mensaje del aviso.';
        } else if (message.length > MESSAGE_MAX) {
            errors.message = `El mensaje no puede superar ${MESSAGE_MAX} caracteres.`;
        }

        if (link && !validUrl(link)) {
            errors.link = 'Ingresa un enlace URL válido.';
        }

        setFormErrors(errors);
        const hasErrors = Object.values(errors).some(Boolean);
        setShowValidationSummary(hasErrors);
        if (hasErrors) {
            scrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
            toast.error(
                Object.values(errors).filter(Boolean)[0] || 'Revisa los campos del aviso.',
            );
        }
        return !hasErrors;
    };

    const handleSave = async () => {
        if (isProcessing) return;
        if (!validateForm()) return;

        setSaving(true);
        try {
            const payload = {
                message: form.message.trim(),
                link: form.link.trim(),
                file: stagedImage?.file || null,
            };

            let result;
            if (isEdit) {
                const hadImage = Boolean(notice?.urlName);
                const removeImage = hadImage && !existingImage && !stagedImage?.file;
                result = await dispatch(updateNoticeMessage(notice._id, {
                    ...payload,
                    removeImage,
                }));
            } else {
                result = await dispatch(createNewNoticeMessage(
                    payload.file,
                    courseId,
                    userId,
                    payload.message,
                    payload.link,
                ));
            }

            if (result?.status === 200 || result?.status === 201) {
                toast.success(result.message || (isEdit ? 'Aviso actualizado' : 'Aviso publicado'));
                await dispatch(getNoticeMessages(courseId));
                resetDraft();
                onClose({ reset: true });
                return;
            }

            toast.error(result?.message || 'No se pudo guardar el aviso.');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudo guardar el aviso.');
        } finally {
            setSaving(false);
        }
    };

    const validationErrorEntries = Object.entries(formErrors).filter(([, msg]) => msg);

    const validationSummary = showValidationSummary && validationErrorEntries.length > 0 ? (
        <div className="task-form__summary" role="alert">
            <p className="task-form__summary-title">
                {validationErrorEntries.length === 1
                    ? 'Falta un campo obligatorio'
                    : `Revisa ${validationErrorEntries.length} campos`}
            </p>
            <ul className="task-form__summary-list">
                {validationErrorEntries.map(([key, msg]) => (
                    <li key={key}>{msg}</li>
                ))}
            </ul>
        </div>
    ) : null;

    return (
        <DrawerCustom
            isOpen={isOpen}
            toggleDrawer={handleDismiss}
            lockClose={isProcessing}
            title={isEdit ? 'Editar aviso' : 'Nuevo aviso'}
            subtitle="Se publica de inmediato y se notifica al grupo"
            footer={(
                <div className="task-form__footer-actions">
                    <Button variant="outline" onClick={handleCancel} disabled={isProcessing}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        className="task-form__submit"
                        onClick={handleSave}
                        loading={saving}
                        loadingLabel={isEdit ? 'Guardando…' : 'Publicando…'}
                    >
                        {isEdit ? 'Guardar cambios' : 'Publicar aviso'}
                    </Button>
                </div>
            )}
        >
            <div className="task-form">
                <div className="task-form__scroll" ref={scrollRef}>
                    <div key={fieldsKey} className="task-form__fields">
                        {validationSummary}

                        <CourseFormSection
                            id="notice-section-content"
                            title="Contenido"
                            description="El aviso se publica al instante. No requiere fecha de activación."
                            icon={<MegaphoneIcon className={SECTION_ICON_CLASS} />}
                        >
                            <TextareaModern
                                id="notice-message"
                                name="message"
                                label="Mensaje"
                                required
                                value={form.message}
                                onChange={(e) => {
                                    setForm((prev) => ({ ...prev, message: e.target.value }));
                                    clearFieldError('message');
                                }}
                                placeholder="Escribe el aviso para el grupo…"
                                maxLength={MESSAGE_MAX}
                                minRows={5}
                                error={formErrors.message}
                            />
                        </CourseFormSection>

                        <CourseFormSection
                            id="notice-section-link"
                            title="Enlace (opcional)"
                            description="Puedes agregar un enlace relacionado con el aviso."
                            icon={<LinkIcon className={SECTION_ICON_CLASS} />}
                        >
                            <InputTextModern
                                id="notice-link"
                                name="link"
                                label="URL"
                                value={form.link}
                                onChange={(e) => {
                                    setForm((prev) => ({ ...prev, link: e.target.value }));
                                    clearFieldError('link');
                                }}
                                placeholder="https://…"
                                autoComplete="off"
                                error={formErrors.link}
                            />
                        </CourseFormSection>

                        <CourseFormSection
                            id="notice-section-image"
                            title="Imagen (opcional)"
                            description={NOTICE_IMAGE_HINT}
                            icon={<PhotoIcon className={SECTION_ICON_CLASS} />}
                        >
                            <TaskAttachmentField
                                label="Imagen del aviso"
                                existing={existingImage}
                                onRemoveExisting={handleRemoveExisting}
                                staged={stagedImage}
                                onSelectFile={handleSelectImage}
                                onClearStaged={handleRemoveStaged}
                                onRemoveStaged={handleRemoveStaged}
                                disabled={isProcessing}
                                multiple={false}
                                maxFiles={1}
                                accept={NOTICE_IMAGE_ACCEPT}
                                hint={NOTICE_IMAGE_HINT}
                            />
                            {formErrors.file ? (
                                <p className="task-form__field-error" role="alert">
                                    {formErrors.file}
                                </p>
                            ) : null}
                        </CourseFormSection>
                    </div>
                </div>
            </div>
        </DrawerCustom>
    );
};
